import React, { useState, useEffect } from 'react';
import { useData } from "../backend/firebase";
// import { db } from "../backend/firebase";
// import { doc, setDoc, onSnapshot, collection } from "firebase/firestore";
import { DateTime } from 'luxon';
import { getImagePath } from '../utils/imageRegistry';

const CaptainPortal = () => {
    const { user, userRole, teams, schedule, toggleCommentary, allLineups, saveMatchLineup } = useData();
    const [adminSelectedTeam, setAdminSelectedTeam] = useState('ALL');
    
    // NEW: Track unsaved changes locally before sending to Firebase
    const [localEdits, setLocalEdits] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const isAdmin = userRole?.role === 'admin';
    const captainTeamName = userRole?.teamName || teams.find(t => t.id === userRole?.teamId)?.name;
    const activeTeamName = isAdmin ? adminSelectedTeam : captainTeamName;

    const resolveTeam = (identifier) => {
        if (!identifier) return { name: "TBA", color: '#555', obj: null };
        const strId = identifier.toString();
        const found = teams.find(t => t.id?.toString() === strId || t.name === identifier);
        return found ? { name: found.name, color: found.color || '#555', obj: found } : { name: identifier, color: '#555', obj: null };
    };

    const TeamIcon = ({ team, size = 32 }) => {
        if (!team) return <div style={{width: size, height: size, background: '#333', borderRadius: '50%'}} />;
        if (team.logoUrl) return <img src={getImagePath(team.logoUrl)} alt={team.name} style={{width: size, height: size, objectFit: 'contain'}} />;
        if (team.logo) return <img src={`data:image/svg+xml;utf8,${encodeURIComponent(team.logo)}`} alt={team.name} style={{width: size, height: size, objectFit: 'contain'}} />;
        return <div style={{width: size, height: size, background: team.color || '#333', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)'}} />;
    };

    // NEW: Handle typing without saving to database
    const handleLocalChange = (matchId, position, value) => {
        setLocalEdits(prev => ({
            ...prev,
            [matchId]: {
                ...(prev[matchId] || {}),
                [position]: value
            }
        }));
    };

    // NEW: The Submit Function
    const submitLineup = async (matchId, teamKey) => {
        const matchEdits = localEdits[matchId];
        if (!matchEdits) return; // Nothing to save

        setIsSaving(true);
        const currentDbData = allLineups[matchId]?.[teamKey] || {};
        const mergedData = { ...currentDbData, ...matchEdits };

        try {
            await saveMatchLineup(matchId, teamKey, mergedData);
            
            // Clear local edits for this match so it falls back to showing DB state
            setLocalEdits(prev => {
                const newState = { ...prev };
                delete newState[matchId];
                return newState;
            });
        } catch (error) {
            console.error("Error saving lineup:", error);
            alert("Failed to save lineup. Check your permissions.");
        }
        setIsSaving(false);
    };

    if (userRole?.role !== 'captain' && userRole?.role !== 'admin') {
        return <div className="PageLayout"><div className="ContentWrapper"><h1 style={{color: 'white'}}>Access Denied</h1></div></div>;
    }

    if (!isAdmin && !activeTeamName) {
        return <div className="PageLayout"><div className="ContentWrapper"><h1 style={{color: 'white'}}>No team assigned. Contact Admin.</h1></div></div>;
    }

    const processedMatches = schedule.map(match => ({
        ...match,
        t1Data: resolveTeam(match.team1),
        t2Data: resolveTeam(match.team2)
    }));

    const displayedMatches = activeTeamName === 'ALL' 
        ? processedMatches 
        : processedMatches.filter(m => m.t1Data.name === activeTeamName || m.t2Data.name === activeTeamName);

    const renderPositionInputs = (match, teamName, teamKey) => {
        const currentDbData = allLineups[match.id]?.[teamKey] || {};
        const currentLocalData = localEdits[match.id] || {};
        const positions = ['North', 'South', 'East', 'West'];
        
        // Check if there are unsaved changes for this specific match
        const hasUnsavedChanges = Object.keys(currentLocalData).length > 0;

        return (
            <div style={{ marginTop: 15, padding: '0 25px 25px 25px' }}>
                <div style={{ fontSize: 13, color: '#FFD700', marginBottom: 12, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Editing Lineup: {teamName}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                    {positions.map(pos => {
                        const posKey = pos.toLowerCase();
                        // If there is a local edit, show it. Otherwise, show what's in the DB.
                        const displayValue = currentLocalData[posKey] !== undefined 
                            ? currentLocalData[posKey] 
                            : (currentDbData[posKey] || "");

                        return (
                            <div key={posKey} style={{ display: 'flex', flexDirection: 'column' }}>
                                <label style={{ fontSize: 11, color: '#8F9EB5', marginBottom: 4, textTransform: 'uppercase', fontWeight: 'bold' }}>
                                    {pos} Player
                                </label>
                                <input 
                                    className="DarkInput"
                                    style={{ margin: 0, padding: '10px 12px' }}
                                    placeholder="Username..."
                                    maxLength={12} // NEW: Max length of 12 characters
                                    value={displayValue}
                                    onChange={(e) => handleLocalChange(match.id, posKey, e.target.value)}
                                />
                            </div>
                        );
                    })}
                </div>
                
                {/* NEW: Submit Button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 15 }}>
                    <button 
                        className="PrimaryButton"
                        onClick={() => submitLineup(match.id, teamKey)}
                        disabled={!hasUnsavedChanges || isSaving}
                        style={{ 
                            background: hasUnsavedChanges ? '#6C5DD3' : 'rgba(255,255,255,0.1)',
                            color: hasUnsavedChanges ? '#fff' : '#8F9EB5',
                            cursor: hasUnsavedChanges ? 'pointer' : 'default',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {isSaving ? "Saving..." : hasUnsavedChanges ? "Save Lineup" : "Saved"}
                    </button>
                </div>
            </div>
        );
    };

    const renderReadOnlyLineup = (match, t1Name, t2Name) => {
        const t1Data = allLineups[match.id]?.team1Lineup || {};
        const t2Data = allLineups[match.id]?.team2Lineup || {};
        const positions = ['north', 'south', 'east', 'west'];

        const formatLineup = (data) => {
            if (typeof data === 'string') return data; 
            const hasData = positions.some(p => data[p]);
            if (!hasData) return <span style={{color: '#555'}}>No lineup entered</span>;
            return positions.map(p => data[p] ? `${p.charAt(0).toUpperCase()}: ${data[p]}` : null).filter(Boolean).join(' | ');
        };

        return (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, padding: '0 25px 25px 25px', fontSize: 13 }}>
                <div style={{ background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)' }}>
                    <strong style={{color: '#fff', fontSize: 15}}>{t1Name}</strong>
                    <div style={{color: '#A0AEC0', marginTop: 8, lineHeight: 1.6}}>{formatLineup(t1Data)}</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)' }}>
                    <strong style={{color: '#fff', fontSize: 15}}>{t2Name}</strong>
                    <div style={{color: '#A0AEC0', marginTop: 8, lineHeight: 1.6}}>{formatLineup(t2Data)}</div>
                </div>
            </div>
        );
    };

    return (
        <div className="PageLayout">
            <div className="ContentWrapper CustomScroll" style={{ overflowY: 'auto' }}>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 15, marginBottom: 30, flexShrink: 0 }}>
                    <h1 className="SectionTitle" style={{ margin: 0 }}>
                        {isAdmin && activeTeamName === 'ALL' ? "Master Lineups" : `Captain Portal: ${activeTeamName}`}
                    </h1>
                    
                    {isAdmin && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(0,0,0,0.3)', padding: '8px 15px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)' }}>
                            <label style={{ color: '#8F9EB5', fontSize: 13, fontWeight: 'bold', textTransform: 'uppercase' }}>Impersonate:</label>
                            <select 
                                className="DarkInput" 
                                style={{ margin: 0, width: 'auto', minWidth: 200, border: 'none', background: 'transparent', padding: 5, color: '#FFD700', fontWeight: 'bold' }}
                                value={adminSelectedTeam}
                                onChange={(e) => setAdminSelectedTeam(e.target.value)}
                            >
                                <option value="ALL">All Matches (View Only)</option>
                                {teams.map(t => (
                                    <option key={t.id} value={t.name}>{t.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {activeTeamName !== 'ALL' && (
                    <p style={{color: '#8F9EB5', marginBottom: 30, flexShrink: 0}}>
                        Assign your players to North, South, East, and West for each match. Hit <strong>Save Lineup</strong> when done. Max 12 characters per player.
                    </p>
                )}
                
                {displayedMatches.length === 0 && <div style={{color: '#8F9EB5', textAlign: 'center', padding: 50, background: 'rgba(0,0,0,0.2)', borderRadius: 12, flexShrink: 0}}>No matches found.</div>}

                {displayedMatches.map(match => {
                    const { t1Data, t2Data } = match;
                    const isTeam1 = t1Data.name === activeTeamName;
                    const teamKey = isTeam1 ? "team1Lineup" : "team2Lineup";

                    let formattedTime = "TBA";
                    if (match.startTime) {
                        const localTime = DateTime.fromISO(match.startTime, { zone: 'America/Los_Angeles' }).toLocal();
                        if (localTime.isValid) formattedTime = localTime.toFormat('cccc, MMM d @ h:mm a');
                    }

                    return (
                        <div key={match.id} className="GlassCard" style={{ flexShrink: 0, marginBottom: 20, padding: 0, overflow: 'hidden' }}>
                            <div style={{ 
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                                background: `linear-gradient(90deg, ${t1Data.color}33 0%, ${t2Data.color}33 100%)`,
                                borderLeft: `6px solid ${t1Data.color}`, borderRight: `6px solid ${t2Data.color}`,
                                padding: '15px 25px', borderBottom: '1px solid rgba(255,255,255,0.05)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '35%' }}>
                                    <TeamIcon team={t1Data.obj} />
                                    <strong style={{color: 'white', fontSize: 16}}>{t1Data.name}</strong>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '30%', textAlign: 'center' }}>
                                    <span style={{color: '#FFD700', fontSize: 14, fontWeight: 'bold'}}>{formattedTime}</span>
                                    <span style={{color: '#8F9EB5', fontSize: 10, textTransform: 'uppercase', marginTop: 2}}>Local Time</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '35%', justifyContent: 'flex-end' }}>
                                    <strong style={{color: 'white', fontSize: 16, textAlign: 'right'}}>{t2Data.name}</strong>
                                    <TeamIcon team={t2Data.obj} />
                                </div>
                            </div>
                            
                            {activeTeamName === 'ALL' 
                                ? renderReadOnlyLineup(match, t1Data.name, t2Data.name)
                                : renderPositionInputs(match, activeTeamName, teamKey)
                            }
                            {activeTeamName === 'ALL' && (
                            <div style={{ padding: '0 25px 25px 25px', display: 'flex', justifyContent: 'flex-end' }}>
                                <div style={{ background: 'rgba(0,0,0,0.3)', padding: 15, borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)', width: '100%', maxWidth: 350 }}>
                                    <div style={{ fontSize: '12px', color: '#8F9EB5', marginBottom: '8px', fontWeight: 'bold' }}>
                                        COMMENTATORS ({match.commentators?.length || 0} / 2)
                                    </div>
                                    
                                    {match.commentators && match.commentators.length > 0 && (
                                        <div style={{ marginBottom: 10, fontSize: 13, color: '#FFD700' }}>
                                            {match.commentators.map((c, idx) => <div key={idx}>🎤 {c}</div>)}
                                        </div>
                                    )}

                                    <button 
                                        onClick={() => toggleCommentary(match.id, user?.email)}
                                        className="PrimaryButton"
                                        style={{ 
                                            background: match.commentators?.includes(user?.email) ? '#E53E3E' : '#26A65B',
                                            margin: 0, width: '100%',
                                            opacity: (match.commentators?.length >= 2 && !match.commentators?.includes(user?.email)) ? 0.5 : 1
                                        }}
                                        disabled={match.commentators?.length >= 2 && !match.commentators?.includes(user?.email)}
                                    >
                                        {match.commentators?.includes(user?.email) ? 'Leave Commentary' : 'Sign up to Commentate'}
                                    </button>
                                </div>
                            </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CaptainPortal;